import { useState } from "react";
import { toast } from "sonner";
import {
  adminAutoAdd,
  adminPrerenderAll,
  adminPrerenderInspect,
  adminRefineTool,
} from "../lib/api";

/**
 * Admin "auto-add" panel. Admin pastes a tool URL; backend scrapes + LLM
 * extracts structured fields and saves as pending. Panel then shows the
 * proposed doc inline so admin can spot bad inferences before approving via
 * the pending tab.
 *
 * Also exposes a "regenerate prerender" button — forces every approved
 * tool to have a fresh per-tool HTML file on disk. Useful when a social
 * share shows the generic homepage card instead of the tool-specific one.
 */
export default function AdminAutoAdd({ onSaved }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagSlug, setDiagSlug] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  // Refine chat state — fresh per scraped tool.
  const [refineInput, setRefineInput] = useState("");
  const [refineHistory, setRefineHistory] = useState([]);
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineError, setRefineError] = useState("");

  const refine = async (e) => {
    e?.preventDefault?.();
    if (!result?.id) return;
    const text = refineInput.trim();
    if (!text || refineBusy) return;
    setRefineError("");
    setRefineBusy(true);
    const optimisticHistory = [...refineHistory, { role: "user", content: text }];
    setRefineHistory(optimisticHistory);
    setRefineInput("");
    try {
      const data = await adminRefineTool(result.id, text, refineHistory);
      setResult(data.tool);
      setRefineHistory([
        ...optimisticHistory,
        { role: "assistant", content: data.notes || "(updated, no notes)" },
      ]);
      toast.success("refined");
      onSaved && onSaved(data.tool);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "refine failed";
      setRefineError(msg);
      // Roll back the optimistic user turn so the user can retry without
      // a stale unanswered entry in the thread.
      setRefineHistory(refineHistory);
    } finally {
      setRefineBusy(false);
    }
  };

  // Reset the refine thread whenever a NEW tool is scraped.
  const resetRefineFor = () => {
    setRefineInput("");
    setRefineHistory([]);
    setRefineError("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = url.trim();
    if (!trimmed) return;
    setBusy(true);
    setResult(null);
    setError("");
    try {
      const data = await adminAutoAdd(trimmed);
      setResult(data);
      resetRefineFor();
      setUrl("");
      toast.success(`saved '${data.title}' as pending`);
      onSaved && onSaved(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "auto-add failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setRegenBusy(true);
    try {
      const data = await adminPrerenderAll();
      if (data.build_dir_exists === false) {
        toast.error(
          "no build dir on this environment — only works on prod after a yarn build",
        );
      } else {
        toast.success(
          `prerender regenerated: ${data.written}/${data.total_approved} files`,
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "regenerate failed");
    } finally {
      setRegenBusy(false);
    }
  };

  const diagnose = async () => {
    // Be forgiving with the input — admin may paste a full URL or
    // a tool's title. Reduce it to a likely slug before sending.
    let slug = diagSlug.trim().toLowerCase();
    // Strip protocol + www.
    slug = slug.replace(/^https?:\/\//, "").replace(/^www\./, "");
    // If it looks like a domain, take the first label.
    if (slug.includes(".")) {
      slug = slug.split(/[./]/)[0];
    }
    // Normalise non-slug chars.
    slug = slug.replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    if (!slug) {
      toast.error("type a tool's slug first (e.g. percept)");
      return;
    }
    setDiagBusy(true);
    setDiagResult(null);
    try {
      // Step 1: force-regen so the file is freshly written, then inspect.
      await adminPrerenderAll();
      const info = await adminPrerenderInspect(slug);
      setDiagResult(info);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "diagnose failed");
    } finally {
      setDiagBusy(false);
    }
  };

  return (
    <section data-testid="admin-auto-add" className="mt-4 border border-black p-4">
      <div className="text-[13px] font-bold mb-2">{">"} ai auto-fill</div>
      <p className="text-[12px] text-[#666] mb-3">
        paste a tool's url. claude scrapes the site + pricing/about subpages
        and proposes title, description, key feature, category, asset class
        and pricing tier. the result is saved as <span className="tv-hl">pending</span> —
        review and approve in the pending tab.
      </p>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="tv-input flex-1 min-w-[280px]"
          required
          disabled={busy}
          data-testid="auto-add-input"
        />
        <button
          type="submit"
          className="tv-btn-primary"
          disabled={busy || !url.trim()}
          data-testid="auto-add-submit"
        >
          {busy ? "[ thinking... ]" : "[ fetch & propose ]"}
        </button>
      </form>
      {busy && (
        <div
          className="text-[12px] text-[#888] mt-3 animate-pulse"
          data-testid="auto-add-busy"
        >
          scraping + extracting... ~15–25s
        </div>
      )}
      {error && (
        <div
          className="text-[12px] text-red-700 mt-3"
          data-testid="auto-add-error"
        >
          {">"} {error}
        </div>
      )}
      {result && (
        <div
          className="mt-4 border border-black p-3 text-[13px] bg-[#fafafa]"
          data-testid="auto-add-result"
        >
          <div className="font-bold mb-1">{result.title}</div>
          <div className="text-[12px] text-[#888] mb-2">{result.url}</div>
          <div className="mb-2">{result.description}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] mb-2">
            <span>[key] <span className="tv-hl">{result.key_feature}</span></span>
            <span>[asset] {result.asset_class}</span>
            <span>
              [function] {result.category}
              {result.category_2 ? `, ${result.category_2}` : ""}
            </span>
            <span>[pricing] {result.pricing}</span>
          </div>
          {result.auto_add_notes && (
            <div
              className="text-[11px] text-[#666] border-t border-[#ddd] pt-2 mt-2"
              data-testid="auto-add-notes"
            >
              <span className="text-[#888]">ai notes:</span>{" "}
              {result.auto_add_notes}
            </div>
          )}
          <div className="text-[11px] text-[#888] mt-2">
            saved as <span className="tv-hl">pending</span>. go to the pending
            tab to review and approve.
          </div>

          {/* Refine chat — admin tells the LLM what it missed, LLM revises */}
          <div className="mt-4 pt-3 border-t border-[#ddd]" data-testid="auto-add-refine">
            <div className="text-[12px] text-[#888] mb-2">
              {">"} refine with ai{" "}
              <span className="text-[#aaa]">— missed something? add or correct details and the ai will revise the fields above.</span>
            </div>

            {refineHistory.length > 0 && (
              <div className="space-y-2 mb-3" data-testid="refine-thread">
                {refineHistory.map((m, i) => (
                  <div key={i} className="text-[12px]">
                    {m.role === "user" ? (
                      <div>
                        <span className="text-[#888]">you:</span>{" "}
                        <span className="text-black">{m.content}</span>
                      </div>
                    ) : (
                      <div className="text-[#444]">
                        <span className="text-[#888]">ai:</span>{" "}
                        <span className="italic">{m.content}</span>
                      </div>
                    )}
                  </div>
                ))}
                {refineBusy && (
                  <div className="text-[12px] text-[#888]">
                    revising<span className="tv-blink">_</span>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={refine} className="flex flex-wrap gap-2 items-stretch">
              <input
                type="text"
                value={refineInput}
                onChange={(e) => setRefineInput(e.target.value)}
                placeholder="e.g. 'pricing is $99/mo not $$$' or 'add that they support options data'"
                className="tv-input flex-1 min-w-[200px] text-[12px]"
                disabled={refineBusy}
                maxLength={1500}
                data-testid="refine-input"
              />
              <button
                type="submit"
                disabled={refineBusy || !refineInput.trim()}
                className="tv-btn-primary"
                data-testid="refine-submit"
              >
                {refineBusy ? "[ thinking... ]" : "[ refine ]"}
              </button>
            </form>

            {refineError && (
              <div className="text-[12px] text-red-700 mt-2" data-testid="refine-error">
                {">"} {refineError}
              </div>
            )}

            {refineHistory.length === 0 && !refineBusy && (
              <div className="text-[11px] text-[#aaa] mt-2">
                examples: "they also do crypto derivatives", "description should mention real-time alerts", "asset class is Multi-asset not just Equities"
              </div>
            )}
          </div>
        </div>
      )}

      {/* Maintenance: regenerate prerender + diagnose stuck share cards.
          Kept separate from the auto-add flow because it's only needed when
          a social share is showing the wrong card. */}
      <div className="mt-6 pt-3 border-t border-[#ddd] text-[12px] text-[#666]">
        <div className="mb-1">{">"} maintenance</div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <button
            type="button"
            onClick={regenerate}
            disabled={regenBusy}
            className="tv-link"
            data-testid="prerender-regenerate"
            title="Force-rewrite every approved tool's static HTML so social shares get the right OG image"
          >
            {regenBusy ? "regenerating..." : "[ regenerate prerender ]"}
          </button>
          <span className="text-[#888]">
            fixes "generic card" issue on x/linkedin shares without redeploy
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={diagSlug}
            onChange={(e) => setDiagSlug(e.target.value)}
            placeholder="e.g. percept"
            className="tv-input text-[12px]"
            disabled={diagBusy}
            data-testid="diagnose-input"
          />
          <button
            type="button"
            onClick={diagnose}
            disabled={diagBusy || !diagSlug.trim()}
            className="tv-link"
            data-testid="diagnose-button"
            title="Regen + read back what the backend has on disk for this slug"
          >
            {diagBusy ? "diagnosing..." : "[ diagnose share card ]"}
          </button>
          <span className="text-[#888]">
            if a specific tool's share card still wrong, type its slug + click
          </span>
        </div>
        {diagResult && (
          <div
            className="mt-3 border border-black p-3 text-[12px] bg-[#fafafa]"
            data-testid="diagnose-result"
          >
            <div className="font-bold mb-2">diagnosis for "{diagResult.slug}":</div>
            <div className="space-y-1 font-mono text-[11px]">
              <div>build dir on disk: <span className="tv-hl">{String(diagResult.build_dir_exists)}</span></div>
              <div>template (index.html) present: <span className="tv-hl">{String(diagResult.template_exists)}</span></div>
              <div>file written at expected path: <span className="tv-hl">{String(diagResult.file_exists)}</span></div>
              {diagResult.file_exists && (
                <>
                  <div>file size: <span className="tv-hl">{diagResult.size_bytes} bytes</span></div>
                  <div>contains per-tool og:image: <span className="tv-hl">{String(diagResult.has_per_tool_title)}</span></div>
                </>
              )}
              <div>expected path: <span className="text-[#888]">{diagResult.expected_path}</span></div>
              {diagResult.sibling_count !== undefined && (
                <div>total tool files on disk: <span className="tv-hl">{diagResult.sibling_count}</span></div>
              )}
            </div>
            <div
              className="mt-3 pt-2 border-t border-[#ddd] text-[12px]"
              data-testid="diagnose-verdict"
            >
              <span className="font-bold">verdict: </span>
              {(() => {
                if (!diagResult.build_dir_exists) {
                  return "no build dir on disk — runtime prerender can't work here. preview-only environment.";
                }
                if (!diagResult.template_exists) {
                  return "build/index.html missing — redeploy needed.";
                }
                if (!diagResult.file_exists) {
                  return "backend can NOT write the file. probably a filesystem permission issue. needs investigation.";
                }
                if (!diagResult.has_per_tool_title) {
                  return "file exists but its content doesn't include the per-tool og:image — write succeeded but data is wrong. send this output to E1.";
                }
                return "file is on the backend's disk with correct content. if x/share still shows the wrong card, the frontend's static server is reading from a different filesystem than the backend writes to — that's an Emergent platform issue, not something we can fix in code. send this output to E1 + Emergent support.";
              })()}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
